/**
 * WhatsNewModal — 릴리즈 노트 모달
 *
 * 두 가지 모드:
 *   - auto: 앱 진입 시 자동 표시 (현재 버전에 릴리즈 노트가 있고, 아직 안 본 경우만)
 *   - history: 프로필에서 호출, 전체 히스토리 아코디언 표시
 */

import { useState, useEffect, useCallback } from 'react';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Sparkles, Zap, Bug, ChevronRight, ScrollText } from 'lucide-react';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '../../components/ui/accordion';
import { Button } from '../../components/ui/button';
import { My } from '../../services/My';
import {
  RELEASE_NOTES,
  findReleaseNote,
  normalizeVersion,
  type ReleaseNote,
  type ReleaseNoteType,
} from '../../const/release-notes';
import package_json from '../../../package.json';

const STORAGE_KEY = 'whats-new-seen-version';

// ── 타입별 아이콘/색상 ──

const TYPE_CONFIG: Record<ReleaseNoteType, { icon: typeof Sparkles; label: string; className: string }> = {
  feature: { icon: Sparkles, label: 'New', className: 'text-emerald-500 bg-emerald-500/10' },
  improvement: { icon: Zap, label: 'Improved', className: 'text-blue-500 bg-blue-500/10' },
  bugfix: { icon: Bug, label: 'Fixed', className: 'text-orange-500 bg-orange-500/10' },
};

// ── 릴리즈 노트 아이템 렌더링 ──

function ReleaseNoteItemView({ item }: { item: ReleaseNote['items'][0] }) {
  const { i18n } = useLingui();
  const config = TYPE_CONFIG[item.type];

  return (
    <div className="py-2">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-meta-sm font-semibold uppercase px-1.5 py-0.5 rounded ${config.className}`}>
            {config.label}
          </span>
          <span className="text-sm font-medium truncate">{i18n._(item.title)}</span>
        </div>
        {item.description && (
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed whitespace-pre-line">
            {i18n._(item.description)}
          </p>
        )}
      </div>
    </div>
  );
}

// ── 단일 릴리즈 노트 뷰 (자동 모드) ──

function SingleReleaseView({ note }: { note: ReleaseNote }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground mb-3">v{note.version} · {note.date}</p>
      {note.items.map((item, i) => (
        <ReleaseNoteItemView key={i} item={item} />
      ))}
    </div>
  );
}

// ── 히스토리 뷰 (전체 아코디언) ──

function HistoryView() {
  if (RELEASE_NOTES.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        <Trans>No release notes available.</Trans>
      </p>
    );
  }

  return (
    <Accordion type="single" collapsible defaultValue={RELEASE_NOTES[0]?.version}>
      {RELEASE_NOTES.map((note) => (
        <AccordionItem key={note.version} value={note.version}>
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-3">
              <span className="font-medium">v{note.version}</span>
              <span className="text-xs text-muted-foreground">{note.date}</span>
              <span className="text-xs text-muted-foreground">
                ({note.items.length} {note.items.length === 1 ? 'change' : 'changes'})
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-1 pl-1">
              {note.items.map((item, i) => (
                <ReleaseNoteItemView key={i} item={item} />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

// ══════════════════════════════════════════════════
// 자동 표시 모드 (App.tsx에서 마운트)
// ══════════════════════════════════════════════════

export function WhatsNewAutoTrigger() {
  const [open, setOpen] = useState(false);
  const [currentNote, setCurrentNote] = useState<ReleaseNote | null>(null);

  useEffect(() => {
    // 릴리즈 노트는 우리 사용자에게 보내는 소식이다 — 통화 링크로 들어온 외부인
    // (비로그인 게스트)에게는 의미가 없고, 통화 화면 위에 뜨면 방해만 된다.
    // 로그인 사용자라도 게스트 통화 페이지(/call/…)에 있는 동안은 띄우지 않는다.
    if (!My.isLoggedIn() || window.location.pathname.startsWith('/call/')) return;

    const currentVersion = normalizeVersion(package_json.version);
    const seenVersion = localStorage.getItem(STORAGE_KEY);

    // 이미 본 버전이면 무시
    if (seenVersion === currentVersion) return;

    // 현재 버전에 릴리즈 노트가 있는지 확인
    const note = findReleaseNote(package_json.version);
    if (!note || note.items.length === 0) {
      // 릴리즈 노트가 없으면 seen 버전만 업데이트하고 종료
      localStorage.setItem(STORAGE_KEY, currentVersion);
      return;
    }

    // 약간의 딜레이 후에 모달 표시 (앱 초기 로딩 완료 후)
    const timer = setTimeout(() => {
      setCurrentNote(note);
      setOpen(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    localStorage.setItem(STORAGE_KEY, normalizeVersion(package_json.version));
  }, []);

  if (!currentNote) return null;
  const hasFeature = currentNote.items.some(i => i.type === 'feature');

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        className="max-w-2xl w-[92vw] max-h-[85vh] p-0 gap-0 flex flex-col overflow-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex-shrink-0">
          <div className="flex items-center gap-2 mb-1">
            <ScrollText className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">
              {hasFeature ? <Trans>What's New</Trans> : <Trans>Update Notes</Trans>}
            </h2>
          </div>
          <p className="text-xs text-muted-foreground">
            {hasFeature ? (
              <Trans>Here's what's new in this update.</Trans>
            ) : (
              <Trans>Here's what's fixed and improved in this update.</Trans>
            )}
          </p>
        </div>

        {/* Content (scrollable) */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">
          <SingleReleaseView note={currentNote} />
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex-shrink-0">
          <Button className="w-full" onClick={handleClose}>
            <Trans>Got it</Trans>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════
// 히스토리 모드 (프로필 모달에서 호출)
// ══════════════════════════════════════════════════

interface WhatsNewHistoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WhatsNewHistoryModal({ open, onOpenChange }: WhatsNewHistoryModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-2xl w-[92vw] max-h-[75vh] p-0 gap-0 flex flex-col overflow-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b flex-shrink-0">
          <div className="flex items-center gap-2 mb-1">
            <ScrollText className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold"><Trans>Release Notes</Trans></h2>
          </div>
          <p className="text-xs text-muted-foreground">
            <Trans>All updates and changes to NEXTPOD.</Trans>
          </p>
        </div>

        {/* Content (scrollable) */}
        <div className="flex-1 overflow-y-auto px-6 py-2">
          <HistoryView />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex-shrink-0">
          <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)}>
            <Trans>Close</Trans>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
